import { isBoolean } from 'lodash';
import React from 'react';
import { Timestamp } from 'firebase/firestore';
import { formatDate, getTime } from './Calendar';

const toProperCase = text => {
  return text.replace(/\w\S*/g, function(txt) {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
  });
};

/*
  When a field has ${source.field1} ${source.Field2} etc
*/
const parseFieldMarkup = ({
  record,
  fieldDataSource = '',
  fieldMarkup,
  useSpan = false,
  returnAsString = false,
  convertBoolToYesNo = true,
  fieldPrefix = '',
  replaceUnMatchedWithEmptyString = true
}) => {
  if (!record) {
    return '';
  }

  try {
    const regEx = /\${(source\..*?)}/g;
    let text = record[fieldDataSource] || record[fieldMarkup] || '';

    let parts = [];

    try {
      parts = [...fieldMarkup.matchAll(regEx)];
    } catch {
      // na
    }

    if (parts.length > 0) {
      text = fieldMarkup;

      parts.forEach(part => {
        const value =
          record[
            part[1].replace(
              `source.${fieldPrefix === '' ? '' : `${fieldPrefix}.`}`,
              ''
            )
          ];

        /* eslint-disable no-template-curly-in-string */
        if (convertBoolToYesNo && isBoolean(value)) {
          if (value) {
            text = text.replace(part[0], 'Yes');
          } else {
            text = text.replace(part[0], 'No');
          }
        } else if (value?.seconds) {
          const ts = new Timestamp(value.seconds, value.nanoseconds);
          text = text.replace(
            part[0],
            `${formatDate(ts.toDate(), '')} ${getTime(ts.toDate())}`
          );
        } else if (value === undefined) {
          if (replaceUnMatchedWithEmptyString) {
            text = text.replace(part[0], '');
          }
        } else {
          text = text.replace(part[0], value);
        }
      });
    }

    if (
      text === '' ||
      !fieldMarkup ||
      fieldMarkup?.matchAll(regEx).length === 0
    ) {
      return fieldMarkup; // just send it back
    }

    /* sqaure brackets can be used to trim spaces on fields that have empty values i.e.
    ${source.firstName} ${source.lastName},
    if lastName is an empty string we want to avoid getting a result like "John ," andinstead have "John,"

    ***** THIS BREAKS on mobile browser because of the regex look ahead
  */
    /*if (text?.toString().indexOf('[') > -1) {
    const regEx2 = /(?<=\[)(.*?)(?=\])/g;
    try {
      const result = [...text.matchAll(regEx2)];

      result.forEach(part => {
        text = text.replace(part[0], part[0].trim());
      });

      if (result.length > 0) {
        text = text.replace(/\[/g, '');
        text = text.replace(/\]/g, '');
      }
    } catch {
      //na
    }
  }*/

    if (text && text.trim) {
      if (text?.trim().lastIndexOf(',') === text?.trim().length - 1) {
        text = text.trim().substr(0, text.trim().length - 1);
      }
    }

    if (returnAsString) {
      return text;
    }

    const items = text.split ? text.split('<br/>') : [text];
    if (useSpan) {
      return items.map(item => <span>{item}</span>);
    } else {
      return items.map(item => <div>{item}</div>);
    }
  } catch (err) {
    // alert(fieldMarkup, err);
  }
};

const stringifySortJson = json => {
  const replacer = (key, value) =>
    value instanceof Object && !(value instanceof Array)
      ? Object.keys(value)
          .sort()
          .reduce((sorted, key) => {
            sorted[key] = value[key];
            return sorted;
          }, {})
      : value;
  return JSON.stringify(json, replacer, 2);
};

const conditionMatches = ({ condition, document }) => {
  const field = condition.field;

  if (condition.operator.toUpperCase() === 'DOES_NOT_START_WITH') {
    return document[field].indexOf(condition.value) !== 0;
  } else if (condition.operator.toUpperCase() === 'IN') {
    let items;
    if (condition.value.indexOf(',') > -1) {
      items = condition.value.split(',');
    } else {
      items = Array.isArray(condition.value)
        ? condition.value
        : JSON.parse(condition.value);
    }

    return items.some(v => v === document[condition.field]);
  } else if (condition.operator.toUpperCase() === 'NOT_IN') {
    let items;
    if (condition.value.indexOf(',') > -1) {
      items = condition.value.split(',');
    } else {
      items = Array.isArray(condition.value)
        ? condition.value
        : JSON.parse(condition.value);
    }

    return !items.some(v => v === document[condition.field]);
  } else if (condition.operator === 'IS_EMPTY') {
    return document[field] === undefined || document[field] === '';
  } else if (condition.operator === 'IS_NOT_EMPTY') {
    if (field.toLowerCase() === 'any') {
      const items = condition.value.split(',');
      return items.every(
        i =>
          document[i] !== undefined &&
          document[i] !== null &&
          document[i] !== ''
      );
    } else {
      return (
        document[field] !== undefined &&
        document[field] !== '' &&
        document[field] !== null
      );
    }
  } else if (
    condition.operator.toUpperCase() === 'GREATER_THAN' ||
    condition.operator === '>'
  ) {
    return parseFloat(document[field]) > condition.value;
  } else if (
    condition.operator.toUpperCase() === 'GREATER_THAN_OR_EQUAL' ||
    condition.operator === '>='
  ) {
    return parseFloat(document[field]) >= condition.value;
  } else if (
    condition.operator.toUpperCase() === 'LESS_THAN' ||
    condition.operator === '<'
  ) {
    return parseFloat(document[field]) < condition.value;
  } else if (
    condition.operator.toUpperCase() === 'LESS_THAN_OR_EQUAL' ||
    condition.operator === '<='
  ) {
    return parseFloat(document[field]) <= condition.value;
  } else if (
    condition.operator === 'DOES_NOT_EQUAL' ||
    condition.operator === '!=' ||
    condition.operator === '!==' ||
    condition.operator === '<>'
  ) {
    return document[field] !== condition.value;
  } else if (condition.operator === 'STARTS_WITH') {
    if (Array.isArray(condition.value)) {
      return condition.value.some(c => document[field].indexOf(c) === 0);
    } else {
      return document[field].indexOf(condition.value) === 0;
    }
  } else {
    if (condition.value === 'true') {
      return document[condition.field] === true;
    } else if (condition.value === 'false') {
      return document[condition.field] === false;
    } else {
      return document[condition.field] === condition.value;
    }
  }
};

export { parseFieldMarkup, stringifySortJson, conditionMatches, toProperCase };
